/**
 * Main backtest service that provides a high-level API for the UI
 */
import { 
  BacktestOptions,
  BacktestResult,
  HistoricalPrice,
  BacktestSignal,
  SymbolSettings
} from './types.js';
import { runBacktest } from './engine.js';
import { AlpacaDataProvider } from './data-providers/alpaca-provider.js';
import { PolygonDataProvider } from './data-providers/polygon-provider.js';
import { BackendHistoricalDataProvider } from './data-providers/backend-provider.js';
import { smaStrategy, meanReversionStrategy } from './strategies/simple-strategy.js';
import { Strategy, Symbol, BacktestSettingsType, Basket } from '../../components/trading/types.js';
import { saveBacktest, updateBacktestStatus, deleteBacktest } from '../backtestStorageService.js';
import { supabase } from '../../integrations/supabase/client.js';
import { MapFunction } from 'typescript';
import { sma } from './indicators/sma.js';
import { ema } from './indicators/ema.js';
import { rsi } from './indicators/rsi.js';
import { SaveBacktestParams } from '../backtestStorageService';

// Cache for historical data to avoid repeated API calls
const dataCache: Record<string, { timestamp: number, data: HistoricalPrice[] }> = {};
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// Create data provider instances - using backend API instead of client-side credentials
const createDataProvider = (provider: string, isPaper: boolean = true) => {
  // Use backend API provider that leverages server-side credentials
  return new BackendHistoricalDataProvider(provider, isPaper);
};

/**
 * Run a backtest with the specified strategy and parameters
 */
export async function runRealisticBacktest(
  strategyId: string,
  symbols: Symbol[],
  settings: BacktestSettingsType,
  modelType: string = 'sma_crossover', // Default strategy type
  strategyName?: string, // Add strategy name parameter
  baskets?: Basket[] // Add baskets parameter
): Promise<BacktestResult & { backtestId?: string }> {
  console.log('Starting realistic backtest with:', { strategyId, symbols: symbols.length, baskets: baskets?.length || 0 });
  
  try {
    // Create backtest record with 'running' status
    let backtestId: string | null = null;
    const user = supabase.auth.getUser();
    const userId = (await user).data.user?.id;
    
    if (userId) {
      // Create a backtest record in the database
      backtestId = await saveBacktest(userId, {
        strategyId: null, // Don't link to strategies table for pure backtests
        name: strategyName || `Backtest ${new Date().toLocaleDateString()}`, // Use strategy name if provided
        type: 'backtest',
        status: 'running',
        startDate: new Date().toISOString(),
        settings,
        results: {
          performance: {
            totalReturn: 0,
            annualizedReturn: 0,
            maxDrawdown: 0,
            sharpeRatio: 0,
            trades: 0,
            winRate: 0,
            profitFactor: 0
          },
          equityCurve: [],
          trades: []
        },
        baskets: baskets || [] // Include baskets in the backtest record
      });
      console.log('Created backtest record with ID:', backtestId);
    }
    
    // Process basket symbols
    let allSymbols = [...symbols];
    if (baskets && baskets.length > 0) {
      // Extract symbols from baskets and add them to allSymbols
      baskets.forEach(basket => {
        if (basket.symbols && basket.symbols.length > 0) {
          // Add symbols from the basket with basket parameters
          basket.symbols.forEach(symbol => {
            // Only add if not already in the list
            if (!allSymbols.some(s => typeof s === 'object' && s.ticker === symbol.ticker)) {
              allSymbols.push({
                ...symbol, 
                // Apply basket-level parameters if symbol doesn't have its own
                profitTarget: symbol.profitTarget || basket.profitTarget,
                stopLoss: symbol.stopLoss || basket.stopLoss,
                valueAtRisk: symbol.valueAtRisk || basket.valueAtRisk
              });
            }
          });
        }
      });
    }
    
    // Convert UI parameters to backtest engine options
    const options = convertToBacktestOptions(allSymbols, settings);
    
    // Fetch historical data
    const historicalData = await getHistoricalData(
      options.symbols,
      options.startDate,
      options.endDate,
      options.timeframe,
      settings.provider || 'alpaca'
    );
    
    if (!historicalData.length) {
      throw new Error('No historical data available for the specified symbols and date range');
    }
    
    // Select signal generator based on model type
    const signalGenerator = getSignalGenerator(modelType);
    
    // Run backtest
    const result = await runBacktest(historicalData, options, signalGenerator);
    
    // Update backtest record with results
    if (backtestId && userId) {
      // First delete the "running" record since it's empty and not needed
      await deleteBacktest(backtestId);
      
      // Now create a new record with all the actual data
      await saveBacktest(userId, {
        strategyId: null, // Don't link to strategies table for pure backtests
        name: strategyName || `Backtest ${new Date().toLocaleDateString()}`,
        type: 'backtest',
        status: 'completed',
        startDate: options.startDate,
        endDate: options.endDate,
        settings: settings,
        results: result,
        baskets: baskets || [] // Include baskets in the final record
      });
      
      console.log('Created completed backtest record after deleting running record');
    }
    
    return {
      ...result,
      backtestId
    };
  } catch (error) {
    console.error('Error running backtest:', error);
    
    // Update backtest status to error
    const user = await supabase.auth.getUser();
    const userId = user.data.user?.id;
    
    if (userId && arguments[0]?.backtestId) {
      await updateBacktestStatus(
        arguments[0].backtestId, 
        'error', 
        error instanceof Error ? error.message : String(error)
      );
    }
    
    throw error;
  }
}

/**
 * Get historical data for the specified symbols and date range
 */
async function getHistoricalData(
  symbols: string[],
  startDate: string,
  endDate: string,
  timeframe: string,
  provider: string
): Promise<HistoricalPrice[]> {
  try {
    // Check cache first
    const cacheKey = `${provider}_${symbols.join(',')}_${startDate}_${endDate}_${timeframe}`;
    const cachedData = dataCache[cacheKey];
    
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_TTL) {
      console.log('Using cached historical data');
      return cachedData.data;
    }
    
    // Fetch from data provider
    const dataProvider = createDataProvider(provider);
    const data = await dataProvider.fetchHistoricalData(symbols, startDate, endDate, timeframe);
    
    // If we got empty data, throw an error to fall back to mock data
    if (!data || !data.length) {
      throw new Error('No historical data returned from provider');
    }
    
    // Cache for future use
    dataCache[cacheKey] = {
      timestamp: Date.now(),
      data
    };
    
    console.log(`Fetched ${data.length} historical data points for ${symbols.length} symbols`);
    return data;
  } catch (error) {
    console.error('Error fetching historical data:', error);
    
    // Return mock data instead of throwing
    console.log('Generating mock historical data after fetch error');
    const mockProvider = new BackendHistoricalDataProvider(provider);
    return mockProvider.generateMockHistoricalData(symbols, startDate, endDate, timeframe);
  }
}

/**
 * Convert UI strategy parameters to backtest engine options
 */
function convertToBacktestOptions(
  symbols: Symbol[],
  settings: BacktestSettingsType
): BacktestOptions {
  // Extract symbols
  const symbolsList = symbols.map(s => typeof s === 'string' ? s : s.ticker || s.symbol || '');
  
  // Enhanced logging for input symbols
  console.log("Symbols received in convertToBacktestOptions:", symbols.map(s => {
    if (typeof s === 'string') return { symbol: s, type: 'string' };
    return { 
      symbol: s.ticker || s.symbol || 'unknown', 
      direction: s.direction || 'unknown',
      profitTarget: s.profitTarget || 'not set', 
      stopLoss: s.stopLoss || 'not set',
      type: 'object'
    };
  }));
  
  // Convert symbol-specific parameters
  const symbolSettings: SymbolSettings[] = symbols
    .filter(s => typeof s !== 'string' && (s.profitTarget || s.stopLoss))
    .map(s => ({
      symbol: typeof s === 'string' ? s : s.ticker || s.symbol || '',
      profitTarget: typeof s !== 'string' ? s.profitTarget : undefined,
      stopLoss: typeof s !== 'string' ? s.stopLoss : undefined
    }));
  
  // Enhanced logging for symbol settings
  console.log("Symbol-specific settings extracted:", symbolSettings);
  
  // Map timeframe format
  const timeframe = mapTimeframe(settings.timeframe || 'day');
  
  // Check for symbol parameters in settings (from backtestingService.ts)
  if (settings.symbolParams && settings.symbolParams.length > 0) {
    console.log("Found symbolParams in settings:", settings.symbolParams);
    
    // Merge with existing symbolSettings
    const existingSymbols = new Set(symbolSettings.map(s => s.symbol));
    settings.symbolParams.forEach(param => {
      if (!existingSymbols.has(param.symbol)) {
        symbolSettings.push({
          symbol: param.symbol,
          profitTarget: param.profitTarget,
          stopLoss: param.stopLoss
        });
      }
    });
    
    console.log("Final merged symbolSettings:", symbolSettings);
  }
  
  return {
    startDate: settings.startDate,
    endDate: settings.endDate,
    timeframe,
    initialCapital: parseFloat(settings.initialCapital || '10000'),
    symbols: symbolsList,
    symbolSettings,
    commissionRate: settings.includeCommission ? parseFloat(settings.commissionRate || '0.0') / 100 : 0,
    slippageRate: settings.includeSlippage ? parseFloat(settings.slippageValue || '0.0') / 100 : 0,
    maxPositionSize: 0.1, // Default to 10% per position
    maxStrategyExposure: 1.0, // Default to 100% strategy exposure
    defaultProfitTarget: 0.1, // Default 10% profit target
    defaultStopLoss: 0.02 // Default 2% stop loss
  };
}

/**
 * Map timeframe from UI format to engine format
 */
function mapTimeframe(timeframe: string): 'minute' | '5minute' | '15minute' | 'hour' | 'day' | 'week' {
  switch (timeframe) {
    case 'minute':
      return 'minute';
    case '5min':
      return '5minute';
    case '15min':
      return '15minute';
    case 'hour':
      return 'hour';
    case 'day':
      return 'day';
    case 'week':
      return 'week';
    default:
      return 'day';
  }
}

/**
 * Get appropriate signal generator based on model type
 */
function getSignalGenerator(
  modelType: string
): (data: HistoricalPrice[], date: string, options: BacktestOptions) => BacktestSignal[] {
  switch (modelType) {
    case 'sma_crossover':
      return smaStrategy;
    case 'mean_reversion':
      return meanReversionStrategy;
    default:
      // Default to SMA strategy
      return smaStrategy;
  }
}

/**
 * Export the main backtest function used by the UI
 * This replaces the random mock implementation in backtestingService.ts
 */
export const realisticBacktester = {
  runBacktest: runRealisticBacktest
};